import { useCallback, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FloatLabel } from "primereact/floatlabel";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import ConfirmationWrapper from "../common/ConfirmationWrapper";
import Loading from "../common/Loading";
import NoContent from "../common/NoContent";
import PageTitle from "../common/PageTitle";
import ProgressiveControl from "../common/ProgressiveControl";
import TabHeader from "../common/TabHeader";
import { useAppContext } from "../../providers/ProviderAppContainer";
import { TEXT_NORMAL, TEXT_SMALL } from "../../style";
import FieldNote from "./common/FieldNote";
import WebsiteMediaInput from "./WebsiteMediaInput";

export default function WebsiteCourses() {
    const { requestAPI, showToast } = useAppContext();
    const [loading, setLoading] = useState();
    const [error, setError] = useState();
    const [rows, setRows] = useState();
    const [dialog, setDialog] = useState({ visible: false });

    const loadRows = useCallback(() => {
        requestAPI({
            requestPath: "website/courses",
            requestMethod: "GET",
            setLoading,
            onRequestStart: setError,
            onRequestFailure: setError,
            onResponseReceieved: (data, responseCode) => {
                if (data && responseCode === 200) setRows(data);
                else setError("Couldn't load Courses");
            },
        });
    }, [requestAPI]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    return (
        <div className="flex flex-column h-full">
            <PageTitle title="Courses" />
            <TabHeader
                className="px-3 pt-3"
                title="Courses"
                highlights={["Each course is one card on the home page and the Courses page"]}
                actionItems={[
                    <Button icon="pi pi-plus" severity="warning" onClick={() => setDialog({ visible: true, record: {} })} />,
                ]}
            />
            <Divider />
            {loading ? <Loading /> : error ? <NoContent error={error} /> : (
                <div className="flex-1 overflow-y-scroll flex flex-column gap-2 p-2">
                    {rows?.length ? rows.map((row) => (
                        <Row key={row.id} row={row} setRows={setRows} requestAPI={requestAPI} showToast={showToast} openEdit={(record) => setDialog({ visible: true, record })} />
                    )) : <NoContent />}
                </div>
            )}
            {dialog.visible ? <Editor dialog={dialog} setDialog={setDialog} setRows={setRows} requestAPI={requestAPI} showToast={showToast} /> : null}
        </div>
    );
}

function Row({ row, setRows, requestAPI, showToast, openEdit }) {
    const [deleting, setDeleting] = useState();
    const remove = () => {
        requestAPI({
            requestPath: `website/courses/${row.id}`,
            requestMethod: "DELETE",
            setLoading: setDeleting,
            parseResponseBody: false,
            onResponseReceieved: (_, responseCode) => {
                if (responseCode === 204) {
                    showToast({ severity: "success", summary: "Deleted", detail: "Courses Deleted", life: 1000 });
                    setRows((prev) => prev?.filter((item) => item.id !== row.id));
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: "Failed To Delete !", life: 2000 });
                }
            },
        });
    };
    const active = row.is_active;
    return (
        <div className={`flex gap-3 align-items-center border-round py-2 px-3 text-white ${active ? "bg-green-500" : "bg-red-500"}`}>
            <div className="flex flex-column flex-1 overflow-hidden">
                <span className={`${TEXT_NORMAL} font-semibold`}>{row.id}. {row.title || "Untitled"}</span>
            </div>
            <i className="pi pi-pencil cursor-pointer" onClick={() => openEdit(row)} />
            <ProgressiveControl loading={deleting} control={<ConfirmationWrapper action={remove}><i className="pi pi-trash cursor-pointer" /></ConfirmationWrapper>} />
        </div>
    );
}

function Editor({ dialog, setDialog, setRows, requestAPI, showToast }) {
    const [record, setRecord] = useState({ is_active: true, is_published: true, ...(dialog.record || {}) });
    const [saving, setSaving] = useState();
    const isNew = !record.id;
    const save = () => {
        requestAPI({
            requestPath: "website/courses",
            requestMethod: isNew ? "POST" : "PATCH",
            requestPostBody: record,
            setLoading: setSaving,
            onRequestFailure: () => showToast({ severity: "error", summary: "Failed", detail: "Failed To Save !", life: 2000 }),
            onResponseReceieved: (saved, responseCode) => {
                if (saved && (responseCode === 200 || responseCode === 201)) {
                    setRows((prev) => (isNew ? [...(prev || []), saved] : prev.map((item) => (item.id === saved.id ? saved : item))));
                    showToast({ severity: "success", summary: "Saved", detail: "Courses Saved", life: 1000 });
                    setDialog({ visible: false });
                } else {
                    showToast({ severity: "error", summary: "Failed", detail: saved?.error || "Failed To Save !", life: 2000 });
                }
            },
        });
    };
    return (
        <Dialog header={isNew ? "Add a course" : "Edit course"} visible className="w-11" onHide={() => setDialog({ visible: false })}>
            <div className="flex flex-column">
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="title" className="w-full" value={record?.title ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, title: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="title">Course name</label>
                        </FloatLabel>
                        <FieldNote text="The heading on the course card." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="subtitle" className="w-full" value={record?.subtitle ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, subtitle: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="subtitle">Line under the name</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="short_description" className="w-full" rows={4} autoResize value={record?.short_description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, short_description: event.target.value }))} disabled={saving} />
                            <label htmlFor="short_description">Short paragraph on the card</label>
                        </FloatLabel>
                        <FieldNote text="Shown on the card. If this is empty, the longer description is used." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputTextarea id="description" className="w-full" rows={4} autoResize value={record?.description || ""} onChange={(event) => setRecord((prev) => ({ ...prev, description: event.target.value }))} disabled={saving} />
                            <label htmlFor="description">Longer description</label>
                        </FloatLabel>
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="category" className="w-full" value={record?.category ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, category: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="category">Filter name</label>
                        </FloatLabel>
                        <FieldNote text="Courses with the same filter name share one chip on the Courses page, such as School or Professional." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="board" className="w-full" value={record?.board ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, board: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="board">Board or badge on the card</label>
                        </FloatLabel>
                        <FieldNote text="Example: GSEB or CBSE. Shown at the top of the card." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="university" className="w-full" value={record?.university ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, university: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="university">University</label>
                        </FloatLabel>
                        <FieldNote text="Used in that same badge when Board is empty. Example: MSU." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="medium" className="w-full" value={record?.medium ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, medium: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="medium">Medium</label>
                        </FloatLabel>
                        <FieldNote text="Example: English or Gujarati. Shown only when the course has no points." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="duration" className="w-full" value={record?.duration ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, duration: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="duration">Duration</label>
                        </FloatLabel>
                        <FieldNote text="Example: 1 Year. Shown only when the course has no points." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="badge" className="w-full" value={record?.badge ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, badge: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="badge">Ribbon on the photo</label>
                        </FloatLabel>
                        <FieldNote text="Shown on the Courses page photo. Leave blank to hide it." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="price" className="w-full" value={record?.price ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, price: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="price">Fee</label>
                        </FloatLabel>
                        <FieldNote text="A number, such as 15000. Leave blank or 0 to hide the fee." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="price_label" className="w-full" value={record?.price_label ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, price_label: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="price_label">Fee text</label>
                        </FloatLabel>
                        <FieldNote text="Optional words instead of the number, such as Fees on request." />
                    </div>
                    <WebsiteMediaInput label="Course photo" field="image" hint="Required size: 1280 ? 720 px (16:9). A larger photo is scaled to fit the Courses page and the course page. The whole photo stays visible. Home page cards do not show this photo." record={record} setRecord={setRecord} disabled={saving} />
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="image_alt" className="w-full" value={record?.image_alt ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, image_alt: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="image_alt">Photo description</label>
                        </FloatLabel>
                        <FieldNote text="Read by screen readers. Describe the photo." />
                    </div>
                    <WebsiteMediaInput label="Brochure PDF" type="pdf" field="brochure_url" hint="Shown as Download Brochure on the Courses page card." record={record} setRecord={setRecord} disabled={saving} />
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="cta_text" className="w-full" value={record?.cta_text ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, cta_text: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="cta_text">Button text</label>
                        </FloatLabel>
                        <FieldNote text="The main button on the card. Leave blank to use View Course Details." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="detail_url" className="w-full" value={record?.detail_url ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, detail_url: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="detail_url">Page the button opens</label>
                        </FloatLabel>
                        <FieldNote text="Example: /courses/11-12-commerce. Leave blank to open /courses." />
                    </div>
                    <div>
                        <FloatLabel className="mt-5">
                            <InputText id="enquiry_url" className="w-full" value={record?.enquiry_url ?? ""} onChange={(event) => setRecord((prev) => ({ ...prev, enquiry_url: event.target.value }))} disabled={saving} pt={{ root: { className: TEXT_NORMAL } }} />
                            <label htmlFor="enquiry_url">Enquiry link</label>
                        </FloatLabel>
                        <FieldNote text="Kept with the course. Example: /contact." />
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="featured" checked={!!record?.featured} onChange={(event) => setRecord((prev) => ({ ...prev, featured: event.checked }))} disabled={saving} />
                        <label htmlFor="featured" className={TEXT_SMALL}>Highlight this card</label>
                    </div>
                    <div className="flex align-items-center gap-2 mt-4">
                        <Checkbox inputId="is_active" checked={!!record?.is_active} onChange={(event) => setRecord((prev) => ({ ...prev, is_active: event.checked }))} disabled={saving} />
                        <label htmlFor="is_active" className={TEXT_SMALL}>Show this course on the website</label>
                    </div>
                <Button className="mt-5 align-self-start" label="Save" icon="pi pi-check" loading={saving} onClick={save} />
            </div>
        </Dialog>
    );
}
